import { API_BASE_URL } from './constants';
import { fetchUserClasses } from './class.service';
import { fetchPrograms } from './program.service';
import { fetchSchedules } from './schedule.service';
import { fetchPartnersOfType } from './specialist.service';
import type { UserClassInterface } from '../types/userClass.interface';
import type { ProgramInterface } from '../types/program.interface';
import type { ClassInterface } from '../types/class.interface';
import { authenticatedFetch } from '@beholos/holoscommon';

// Dashboard data interfaces
export interface DashboardUserData {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  totalMinutesTrained: number;
  totalCaloriesBurned: number;
  BMI: number;
  passesRemaining: number;
  plan: {
    id: string;
    name: string;
    numberOfPasses: number;
    liveStreamOnly: boolean;
    price: number;
    description: string;
  };
  address: {
    countryCode: string;
    country: string;
    city: string;
    district: string;
    street: string;
  };
}

export interface DashboardData {
  userClasses: UserClassInterface[];
  featuredPrograms: ProgramInterface[];
  videoCategories: any[];
  individualSessions: any[];
  recommendedClasses: ClassInterface[];
  dashboardBanners: any[];
  notifications: any[];
}

// Get user's upcoming classes
export const fetchDashboardUserClasses = async (): Promise<UserClassInterface[]> => {
  try {
    const { classes } = await fetchUserClasses({ 
      future: true, 
      limit: 5 
    });
    return classes;
  } catch (error) {
    console.error('Error fetching user classes:', error);
    return [];
  }
};

// Get featured programs for dashboard
export const fetchFeaturedPrograms = async (): Promise<ProgramInterface[]> => {
  try {
    const programs = await fetchPrograms();
    return programs.slice(0, 3); // Return first 3 programs
  } catch (error) {
    console.error('Error fetching featured programs:', error);
    return [];
  }
};

// Get video categories for dashboard
export const fetchVideoCategories = async (): Promise<any[]> => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/videos/categories`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching video categories:', error);
    return [];
  }
};

// Get individual sessions (specialists)
export const fetchIndividualSessions = async (): Promise<any[]> => {
  try {
    const nutritionists = await fetchPartnersOfType('showNutritionist');
    const healthCoaches = await fetchPartnersOfType('showHealthCoach');
    const psychologists = await fetchPartnersOfType('showPsychologist');
    const financialAdvisors = await fetchPartnersOfType('showFinancialAdvisor');

    const sessions = [
      ...nutritionists.map(p => ({ ...p, type: 'nutritionist' })),
      ...healthCoaches.map(p => ({ ...p, type: 'healthCoach' })),
      ...psychologists.map(p => ({ ...p, type: 'psychologist' })),
      ...financialAdvisors.map(p => ({ ...p, type: 'financialAdvisor' }))
    ];

    return sessions;
  } catch (error) {
    console.error('Error fetching individual sessions:', error);
    return [];
  }
};

// Get recommended classes for dashboard
export const fetchRecommendedClasses = async (): Promise<ClassInterface[]> => {
  try {
    const classes = await fetchSchedules({ 
      limit: 6,
      liveStreamClass: false 
    });
    return classes;
  } catch (error) {
    console.error('Error fetching recommended classes:', error);
    return [];
  }
};

// Get dashboard banners
export const fetchDashboardBanners = async (): Promise<any[]> => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/dashboard/banners`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching dashboard banners:', error);
    return [];
  }
};

// Get user notifications
export const fetchUserNotifications = async (): Promise<any[]> => {
  try {
    const token = typeof window !== 'undefined' ? sessionStorage.getItem('token') : null;
    const response = await authenticatedFetch(`${API_BASE_URL}/notifications`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching user notifications:', error);
    return [];
  }
};

// Get programs for home page
export const fetchHomePrograms = async (): Promise<ProgramInterface[]> => {
  try {
    // Make API call to get all programs
    const response = await authenticatedFetch(`${API_BASE_URL}/programs`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const allPrograms = await response.json();
    
    // Randomly select 6 programs from the response
    const shuffled = [...allPrograms.programs].sort(() => 0.5 - Math.random());
    const selectedPrograms = shuffled.slice(0, 6);
    
    return selectedPrograms;
  } catch (error) {
    console.error('Error fetching home programs:', error);
    return [];
  }
};

// Main dashboard data aggregator
export const fetchDashboardData = async (): Promise<DashboardData> => {
  try {
    //todo delete unused calls
    const [
      userClasses,
      featuredPrograms,
      videoCategories,
      individualSessions,
      recommendedClasses,
      dashboardBanners,
      notifications
    ] = await Promise.all([
      fetchDashboardUserClasses(),
      fetchFeaturedPrograms(),
      fetchVideoCategories(),
      fetchIndividualSessions(),
      fetchRecommendedClasses(),
      fetchDashboardBanners(),
      fetchUserNotifications()
    ]);

    const dashboardData: DashboardData = {
      userClasses,
      featuredPrograms,
      videoCategories,
      individualSessions,
      recommendedClasses,
      dashboardBanners,
      notifications
    };

    return dashboardData;
  } catch (error) {
    console.error('Error fetching dashboard data:', error);
    throw error;
  }
};

// Submit partner suggestion
export const submitPartnerSuggestion = async (suggestionData: {
  partnerName: string;
  description: string;
  contactInfo: string;
}): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/partners/suggest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(suggestionData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return true;
  } catch (error) {
    console.error('Error submitting partner suggestion:', error);
    return false;
  }
}; 